// apps/admin/src/api/auditLog.api.ts
//
// Phase 3.4a Wave 9 Task 24 — Admin audit-log API client.
//
// Mirrors the GET /api/v1/admin/audit-log contract implemented by Wave 5
// Task 15 (apps/api/src/modules/audit/handlers/audit-log.handlers.ts):
//
//   ?actor=<userId>           exact ObjectId match on actorId
//   ?action=<verb>            exact match on action code
//   ?entityType=<type>        exact match on targetType
//   ?from=<ISO8601>           createdAt >= from
//   ?to=<ISO8601>             createdAt <= to
//   ?q=<text>                 regex match on actor.email|action|entityId
//   ?cursor=<base64>          opaque pagination cursor
//   ?limit=<n>                default 50, capped at 200
//   ?format=csv               toggles streaming CSV response
//
// Per CONTEXT.md
//   • P3.4a. — read-only audit log surface (no DELETE/PATCH client paths).
//   • P3.4a. — immutable insert-only (this client never mutates).
//   • P3.4a. — RBAC: caller must hold `audit:view_admin`.
//   • P3.4a. — CSV export streams server-side; client uses direct
//                   browser navigation to the URL so the response is not
//                   buffered in memory.

import { apiClient, resolveApiBaseUrl } from '@printway/shared/api';

// ---------------------------------------------------------------------------
// Wire types — slim mirror of audit-log.handlers.ts AuditRowJson.
// ---------------------------------------------------------------------------

export interface AuditLogEntry {
  id: string;
  action: string;
  actorId: string;
  actorEmail: string;
  actorRole: string;
  targetType: string;
  targetId: string;
  category: string;
  oldValue: Record<string, unknown>;
  newValue: Record<string, unknown>;
  requestId: string | null;
  merchantId: string | null;
  metadata: Record<string, unknown>;
  ip: string;
  userAgent: string;
  sessionId: string;
  createdAt: number;
}

export interface AuditLogQueryParams {
  actor?: string;
  action?: string;
  entityType?: string;
  from?: string;
  to?: string;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface AuditLogListResponse {
  entries: AuditLogEntry[];
  nextCursor: string | null;
  hasMore: boolean;
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  requestId: string;
}

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
  requestId: string;
}

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as SuccessEnvelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

function envelopeError(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    return (payload as ErrorEnvelope).error?.message ?? null;
  }
  return null;
}

/**
 * Build a `URLSearchParams` from an `AuditLogQueryParams`. Skips empty /
 * undefined values so the resulting query string only carries active
 * filters — matters for the URL deeplink contract.
 */
function buildSearchParams(params: AuditLogQueryParams): URLSearchParams {
  const sp = new URLSearchParams();
  if (params.actor) sp.set('actor', params.actor);
  if (params.action) sp.set('action', params.action);
  if (params.entityType) sp.set('entityType', params.entityType);
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  if (params.q) sp.set('q', params.q);
  if (params.cursor) sp.set('cursor', params.cursor);
  if (typeof params.limit === 'number') sp.set('limit', String(params.limit));
  return sp;
}

// Narrow the openapi-fetch client surface to the verb we use here so call
// sites stay typed without reaching into types.generated.ts.
type RawClient = {
  GET: (
    path: string,
    init?: { headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

/**
 * Fetch a paginated page of audit entries. Throws on network failure or
 * non-2xx response with the server-supplied message when present.
 */
export async function listAuditLog(
  params: AuditLogQueryParams,
): Promise<AuditLogListResponse> {
  const client = apiClient() as unknown as RawClient;
  const qs = buildSearchParams(params).toString();
  const path = qs.length > 0
    ? `/api/v1/admin/audit-log?${qs}`
    : '/api/v1/admin/audit-log';
  const { data, error, response } = await client.GET(path);
  if (error || !response.ok) {
    const msg = envelopeError(error) ?? `HTTP ${response.status}`;
    throw new Error(msg);
  }
  // Server returns { items, nextCursor, hasMore }; map `items` → `entries`
  // for the FE convention used elsewhere (TopupQueuePage, TeamsPage).
  const payload = unwrap<{
    items: AuditLogEntry[];
    nextCursor: string | null;
    hasMore: boolean;
  }>(data);
  return {
    entries: payload.items ?? [],
    nextCursor: payload.nextCursor ?? null,
    hasMore: payload.hasMore ?? false,
  };
}

/**
 * Build the absolute URL for the streaming CSV export. The caller assigns
 * this to `window.location.href` so the browser handles the streaming
 * download — no client-side concat (P3.4a. + acceptance criterion).
 */
export function getAuditLogCsvUrl(params: AuditLogQueryParams): string {
  const sp = buildSearchParams(params);
  sp.set('format', 'csv');
  // Drop pagination params for export — server streams the full filtered set.
  sp.delete('cursor');
  sp.delete('limit');
  const base = resolveApiBaseUrl();
  return `${base}/api/v1/admin/audit-log?${sp.toString()}`;
}

// ---------------------------------------------------------------------------
// Static enums — populated from server-side action / targetType usage so
// the multi-select dropdowns work without an extra distinct-values call.
// ---------------------------------------------------------------------------

/** Action codes emitted by services that call `auditLog.append({ action })`. */
export const AUDIT_ACTION_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  // Auth + setup
  { value: 'AUTH_LOGIN', label: 'Auth — Login' },
  { value: 'AUTH_LOGOUT', label: 'Auth — Logout' },
  { value: 'AUTH_REGISTER', label: 'Auth — Register' },
  { value: 'AUTH_OTP_VERIFY', label: 'Auth — OTP Verify' },
  { value: 'AUTH_OTP_RESEND', label: 'Auth — OTP Resend' },
  { value: 'AUTH_PASSWORD_CHANGE', label: 'Auth — Password Change' },
  { value: 'AUTH_PASSWORD_RESET', label: 'Auth — Password Reset' },
  { value: 'AUTH_PASSWORD_RESET_REQUESTED', label: 'Auth — Password Reset Requested' },
  { value: 'AUTH_REFRESH', label: 'Auth — Token Refresh' },
  { value: 'AUTH_TOKEN_REUSE', label: 'Auth — Token Reuse Detected' },
  { value: 'AUTH_OAUTH_LOGIN_INITIATED', label: 'Auth — OAuth Login Initiated' },
  { value: 'AUTH_OAUTH_CALLBACK_SUCCESS', label: 'Auth — OAuth Callback Success' },
  { value: 'AUTH_OAUTH_CALLBACK_FAILED', label: 'Auth — OAuth Callback Failed' },
  { value: 'AUTH_SIGNUP_COUNTRY_MISMATCH', label: 'Auth — Signup Country Mismatch' },
  { value: 'AUTH_SURVEY_SUBMIT', label: 'Auth — Survey Submit' },
  // Impersonate
  { value: 'AUTH_IMPERSONATE_START', label: 'Impersonate — Start' },
  { value: 'AUTH_IMPERSONATE_END', label: 'Impersonate — End' },
  { value: 'AUTH_IMPERSONATE_LANDING', label: 'Impersonate — Landing' },
  { value: 'AUTH_EMERGENCY_MODE_ACTIVATE', label: 'Impersonate — Emergency Mode' },
  // Setup
  { value: 'SETUP_INIT', label: 'Setup — Init' },
  { value: 'SETUP_COMPLETED', label: 'Setup — Completed' },
  // Team
  { value: 'AUTH_TEAM_INVITE', label: 'Team — Invite' },
  { value: 'AUTH_TEAM_INVITE_COMPLETE', label: 'Team — Invite Complete' },
  { value: 'AUTH_TEAM_SUSPEND', label: 'Team — Suspend' },
  { value: 'AUTH_TEAM_UNSUSPEND', label: 'Team — Unsuspend' },
  { value: 'AUTH_TEAM_REASSIGN', label: 'Team — Reassign' },
  { value: 'MEMBER_SOFT_DELETED', label: 'Member — Soft Deleted' },
  { value: 'MEMBER_UPDATED', label: 'Member — Updated' },
  // Limit Request
  { value: 'LIMIT_REQUEST_SUBMITTED', label: 'Limit Request — Submitted' },
  { value: 'LIMIT_REQUEST_APPROVED', label: 'Limit Request — Approved' },
  { value: 'LIMIT_REQUEST_REJECTED', label: 'Limit Request — Rejected' },
  { value: 'LIMIT_REQUEST_CANCELLED', label: 'Limit Request — Cancelled' },
  // 2FA / security
  { value: 'TWO_FA_RESET_BY_ADMIN', label: '2FA — Reset by Admin' },
  // Billing / ledger
  { value: 'CASHBACK_CREDIT', label: 'Billing — Cashback Credit' },
  { value: 'REFUND_CREATE', label: 'Billing — Refund Create' },
  { value: 'INTERNAL_AUTH_REJECTED', label: 'Internal — Auth Rejected' },
];

/** Target entity types referenced by audit appends.
 *
 * Synced with `apps/api/src/modules/audit/audit-entity-types.ts` —
 * `PHASE_8_3_AUDIT_ENTITY_TYPES` (24 new types
 * appended 2026-05-18 via /vg:debug 8.3 Cluster quick-wins fix for
 * F-AUDIT-FILTER-001 (FE chips were stuck on Phase 1 types only).
 */
export const AUDIT_ENTITY_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  // Phase 1 baseline types — kept for legacy audit rows.
  { value: 'User', label: 'User' },
  { value: 'Team', label: 'Team' },
  { value: 'TeamMember', label: 'Team Member' },
  { value: 'Merchant', label: 'Merchant' },
  { value: 'Role', label: 'Role' },
  { value: 'Permission', label: 'Permission' },
  { value: 'LimitRequest', label: 'Limit Request' },
  { value: 'TopupRequest', label: 'Topup Request' },
  { value: 'WithdrawRequest', label: 'Withdraw Request' },
  { value: 'Chargeback', label: 'Chargeback' },
  { value: 'Wallet', label: 'Wallet' },
  { value: 'Session', label: 'Session' },

  // Phase 8.3 baseline types.
  { value: 'Orders', label: 'Orders' },
  { value: 'Users', label: 'Users (admin staff)' },
  { value: 'Products', label: 'Products' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Merchants', label: 'Merchants' },
  { value: 'Vendors', label: 'Vendors' },
  { value: 'Stores', label: 'Stores' },
  { value: 'SupportTickets', label: 'Support Tickets' },
  { value: 'Settings', label: 'System Settings' },
  { value: 'FeatureFlags', label: 'Feature Flags' },
  { value: 'KYCDocuments', label: 'KYC Documents' },
  { value: 'Payouts', label: 'Payouts' },
  { value: 'ImpersonationSessions', label: 'Impersonation Sessions' },
  { value: 'ApprovalRequests', label: 'Approval Requests' },

  // Phase 8.3 catalog extension types.
  { value: 'Addons', label: 'Add-ons' },
  { value: 'BlueprintsAdmin', label: 'Blueprints (admin)' },
  { value: 'Bundles', label: 'Bundles' },
  { value: 'Categories', label: 'Categories' },
  { value: 'Collections', label: 'Collections' },
  { value: 'CreditMemos', label: 'Credit Memos' },
  { value: 'LegalEntities', label: 'Legal Entities' },
  { value: 'PendingApprovals', label: 'Pending Approvals' },
  { value: 'PrepaymentRequests', label: 'Prepayment Requests' },
  { value: 'Pricing', label: 'Pricing' },
  { value: 'ProductsSpecial', label: 'Products Special' },
  { value: 'RefundTasks', label: 'Refund Tasks' },
  { value: 'RegionErrors', label: 'Region Errors' },
  { value: 'Templates', label: 'Templates' },
  { value: 'Techniques', label: 'Techniques' },
  { value: 'Tickets', label: 'Tickets' },
  { value: 'TicketMessages', label: 'Ticket Messages' },
];

/** Date-range presets — values are durations in days; `custom` opens pickers. */
export const AUDIT_DATE_PRESETS: ReadonlyArray<{
  value: '7d' | '30d' | '90d' | 'custom';
  label: string;
  days: number | null;
}> = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
  { value: 'custom', label: 'Custom range', days: null },
];
